import { OverclaimWorkSearchQuery } from '@App/stores/search/models/overclaim-search'
import { OverclaimFilterType } from '@App/stores/overclaim/overclaim-search-store'
import { useContext, useEffect, useState } from 'react'
import { StoresContext } from '@App/contexts'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { OverclaimFilterCount } from '@Entities/overclaim/overclaim-status-count'
import { ExportType } from '@App/enum/export-type'
import { defaultCatalogPagination } from '@App/stores/search/work/catalog-search-store'
import { useBulkActionNavigation } from '@Components/BulkAction/hooks/useBulkActionNavigation'
import { bulkActionLabels } from '@App/stores/overclaim/overclaim-search-bulk-action-store'
import { PaginationBody } from '@Entities/pagination'
import { FilterValues } from '@Components/Search/SearchPanel/types'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

export const useOverclaimExport = () => {
    const { exportStore } = useContext(StoresContext)
    const [isOpen, setIsOpen] = useState(false)

    const close = () => setIsOpen(false)
    const open = () => setIsOpen(true)

    useEffect(() => {
        exportStore.setExportType(ExportType.OVERCLAIMS)
        exportStore.loadExportFormats()
    }, [])

    return {
        exportSidebarIsOpen: isOpen,
        setExportSidebarIsOpen: setIsOpen,
        openExportSidebar: open,
        closeExportSidebar: close,
    }
}

export const useOverclaimSearch = (
    initialFilters?: FilterValues<OverclaimFilterType>,
    initialCriteria?: Record<string, unknown>
) => {
    const { overclaimSearchStore, overclaimViewStore } = useContext(StoresContext)

    // Built lazily and seeded before the form first renders, which is when it picks up the rows.
    const [criteriaStore, setCriteriaStore] = useState<SearchCriteriaStore<OverclaimWorkSearchQuery>>(() => {
        const store = overclaimSearchStore.createOverclaimCriteriaStore()
        if (initialCriteria) {
            store.applyCriteriaFromObject(initialCriteria)
        }
        return store
    })

    const [filterCount, setFilterCount] = useState<OverclaimFilterCount | undefined>()
    const [loading, setLoading] = useState(false)

    const runSearch = async (pagination: PaginationBody = defaultCatalogPagination) => {
        setLoading(true)
        await overclaimSearchStore.searchForWorks(pagination)
        setLoading(false)
    }

    const runSearchWithNewCriteria = async (searchCriteria: OverclaimWorkSearchQuery, filters: OverclaimFilterType) => {
        overclaimSearchStore.setOverclaimSelectionSearchQuery(searchCriteria)
        overclaimSearchStore.setCurrentSearchFilters(filters)
        await runSearch()
    }

    const onChangeLimit = async (newLimit: number) => {
        const newPagination = overclaimSearchStore.changeSearchResultsLimit(newLimit)
        await runSearch(newPagination)
    }

    const reset = async () => {
        setCriteriaStore(overclaimSearchStore.createOverclaimCriteriaStore())
        overclaimSearchStore.resetStore()
        await runSearch()
    }

    const loadStatusCounts = async () => {
        await overclaimSearchStore.setupOverclaimStatusCounts()
        setFilterCount(overclaimSearchStore.overclaimStatusCounts)
    }

    const hasFilters = [
        overclaimSearchStore.currentSearchFilters.amendmentStatus,
        overclaimSearchStore.currentSearchFilters.documentationStatus,
        overclaimSearchStore.currentSearchFilters.status,
    ].some((filter) => filter?.length)

    const init = async () => {
        overclaimViewStore.destroyOverclaimViewItem()
        await loadStatusCounts()

        // Seeds the very first search with filters carried in via the URL (e.g. a dashboard card
        // linking straight into "Overclaim + Document Needed"), before the default-pagination search below runs.
        if (initialFilters) {
            // Parsed from the URL as a loose DeepPartial; every value present is a concrete string
            // the store's own filter definitions produced, so this narrowing is safe.
            overclaimSearchStore.setCurrentSearchFilters(initialFilters as OverclaimFilterType)
        }

        // Same for search terms carried in the URL: what the store applied is what the first search uses,
        // so a term the criteria store dropped (an unknown key, a second primary option) isn't searched.
        if (initialCriteria) {
            overclaimSearchStore.setOverclaimSelectionSearchQuery(mapCriteriaToObject(criteriaStore.searchCriteria))
        }

        if (overclaimSearchStore.isDefaultPagination) {
            await runSearch({
                limit: overclaimSearchStore.pagination.limit,
                page: overclaimSearchStore.pagination.page,
            })
        }
    }

    useEffect(() => {
        init()
    }, [])

    return {
        criteriaStore,
        filterCount,
        loading,
        runSearch,
        runSearchWithNewCriteria,
        onChangeLimit,
        reset,
        hasFilters,
    }
}

export const useRightsholderOverclaimSearch = (
    initialFilters?: FilterValues<OverclaimFilterType>,
    initialCriteria?: Record<string, unknown>
) => {
    const { overclaimSearchBulkActionStore, overclaimSearchStore } = useContext(StoresContext)
    const search = useOverclaimSearch(initialFilters, initialCriteria)
    const overclaimExport = useOverclaimExport()

    const { blocker, bulkActionLabel } = useBulkActionNavigation(
        overclaimSearchBulkActionStore,
        overclaimSearchStore,
        bulkActionLabels
    )

    return {
        ...search,
        ...overclaimExport,
        blocker,
        bulkActionLabel,
    }
}
